import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "@/components/common/SEO";
import PageHeader from "@/components/shared/PageHeader";
import { Reveal, staggerItem } from "@/components/common/motion";
import { ContactCTA } from "@/components/shared/blocks";
import { technologies, techCategories } from "@/data/site";
import { cn } from "@/lib/utils";

export default function Technologies() {
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? technologies : technologies.filter((t) => t.category === filter);

  return (
    <>
      <SEO title="Technologies" description="Our modern technology stack across frontend, backend, mobile, cloud, DevOps, data and AI." path="/technologies" />
      <PageHeader
        overline="Technology Stack"
        title="Modern tools, chosen for the job"
        subtitle="We stay pragmatic — using proven, high-performance technologies that scale with your business."
      />

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap gap-2" data-testid="tech-filters">
            {techCategories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`tech-filter-${c.toLowerCase()}`}
                className={cn(
                  "rounded-full border px-5 py-2 text-sm font-medium transition-colors",
                  filter === c ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                )}
              >
                {c}
              </button>
            ))}
          </div>

          <motion.div layout className="mt-12 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            <AnimatePresence mode="popLayout">
              {list.map((t) => (
                <motion.div
                  key={t.name}
                  layout
                  variants={staggerItem}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.35 }}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg"
                  data-testid={`tech-item-${t.name.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="h-3 w-3 rounded-full bg-gradient-to-br from-primary to-accent" />
                    <span className="font-display text-lg font-medium">{t.name}</span>
                  </div>
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t.category}</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <ContactCTA />
    </>
  );
}
